import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { PHOTO_LANE, UploadEngine } from '@lite-workspace/uploader-core';
import { firstValueFrom } from 'rxjs';
import type {
  RegisterOutcome,
  RegisterOutcomeStatus,
  SyncPhotoStatus,
  SyncResult,
  UploadFailureReason,
  UploadResult,
} from '../entities/upload.models';
import type { EnsureProjectInfo, RegisterPhotoInput, SharpeningLevel, UploadOpts } from './gallery-upload.data';

const SHARPENING_LEVELS: readonly SharpeningLevel[] = ['none', 'low', 'optimal', 'high'];

interface WireRegisterResult {
  results: Array<{ fileId: string; status: string; photoId: string | null; sortOrder: number | null }>;
}

interface WireSyncResult {
  photos: Record<string, { status: string }>;
  exifBacklog?: boolean;
}

@Injectable({ providedIn: 'root' })
export class GalleryUploadApiService {
  private readonly http = inject(HttpClient);
  private readonly engine = inject(UploadEngine);

  async ensureProject(galleryId: string): Promise<EnsureProjectInfo | null> {
    try {
      const wire = await firstValueFrom(
        this.http.post<{
          lumirProjectId: string;
          faceRecognitionEnabled?: boolean;
          rawUploadEnabled?: boolean;
          sharpeningLevel?: string;
        }>(`/api/gallery-uploads/${encodeURIComponent(galleryId)}/project`, {}),
      );
      if (!wire.lumirProjectId) return null;
      return {
        lumirProjectId: wire.lumirProjectId,
        faceRecognitionEnabled: wire.faceRecognitionEnabled === true,
        rawUploadEnabled: wire.rawUploadEnabled === true,
        sharpeningLevel: SHARPENING_LEVELS.includes(wire.sharpeningLevel as SharpeningLevel)
          ? (wire.sharpeningLevel as SharpeningLevel)
          : 'optimal',
      };
    } catch {
      return null;
    }
  }

  // The shared engine owns the multipart contract (processMetadata before the file field), the slots,
  // the network retry and the byte progress. The answer's storage fields give the file's public URL,
  // so a native-video upload can persist it as its source; the photo path just ignores fileUrl.
  async upload(projectId: string, file: File, fileId: string, opts?: UploadOpts): Promise<UploadResult> {
    try {
      const stored = await this.engine.uploadAndWait({
        lane: PHOTO_LANE,
        key: projectId,
        file,
        meta: {
          projectId,
          fileId,
          fileTags: [],
          // A file the project already holds (a photo whose register never landed, or the same frame
          // dropped twice) comes back as that file's own FileDto instead of the uploader's empty
          // duplicate answer — the queue then registers the stored copy. The uploader looks for the
          // duplicate inside this project only, so a file another project holds is simply stored again.
          reuseDuplicate: true,
          // Consent-off galleries must not form persons from detected faces (spec 13 consent gate).
          ...(opts?.notGenerateNewPersons ? { notGenerateNewPersons: true } : {}),
          // The uploader copies processMetadata into the saga, which rides every Kafka hop — that's how
          // the account's sharpening level (spec 02 §5) reaches the preview cutter.
          ...(opts?.sharpening && opts.sharpening !== 'none' ? { sharpening: opts.sharpening } : {}),
        },
        onProgress: percent => opts?.onProgress?.(Math.min(100, percent)),
      });
      // Empty = nothing stored under our fileId; registering it would leave a row pending forever.
      // A duplicate comes back as its FileDto, so empty means the uploader dropped a file it could
      // not decode.
      if (!stored) return { ok: false, reason: 'processing' };
      return { ok: true, fileUrl: stored.url, fileId: stored.id };
    } catch (error) {
      return { ok: false, reason: toFailureReason(error) };
    }
  }

  /** Aborts what this project has on the wire: the batch was cancelled. */
  cancelUploads(projectId: string): void {
    this.engine.cancelKey(projectId);
  }

  async register(galleryId: string, photos: RegisterPhotoInput[]): Promise<RegisterOutcome[] | null> {
    try {
      const wire = await firstValueFrom(
        this.http.post<WireRegisterResult>(`/api/gallery-uploads/${encodeURIComponent(galleryId)}/register`, {
          photos,
        }),
      );
      return wire.results.map(result => ({
        fileId: result.fileId,
        status: toRegisterStatus(result.status),
        photoId: result.photoId,
      }));
    } catch {
      return null;
    }
  }

  async syncStatus(galleryId: string): Promise<SyncResult | null> {
    try {
      const wire = await firstValueFrom(
        this.http.post<WireSyncResult>(`/api/gallery-uploads/${encodeURIComponent(galleryId)}/sync-status`, {}),
      );
      return {
        photos: Object.fromEntries(
          Object.entries(wire.photos).map(([photoId, entry]) => [photoId, { status: toSyncStatus(entry.status) }]),
        ),
        exifBacklog: wire.exifBacklog === true,
      };
    } catch {
      return null;
    }
  }
}

function toFailureReason(error: unknown): UploadFailureReason {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 413) return 'too_large';
    if (error.status === 400 || error.status === 415) return 'unsupported_type';
    if (error.status === 0) return 'network';
  }

  return 'unknown';
}

function toRegisterStatus(status: string): RegisterOutcomeStatus {
  switch (status) {
    case 'registered':
      return 'created';
    // Already a photo of this gallery: not a fresh upload, and not a failure either.
    case 'already_registered':
      return 'duplicate';
    case 'scene_mismatch':
      return 'scene_mismatch';
    // `refused` and any word this client does not know: never a success.
    default:
      return 'refused';
  }
}

function toSyncStatus(status: string): SyncPhotoStatus {
  return status === 'ready' || status === 'failed' ? status : 'pending';
}
